import React from 'react';
import { Button, Checkbox, Form, Input, Select, Switch } from 'antd';
import { openNotification, toTurkishUpper } from '../../../utils';
import { apiFetch } from 'services/apiClient';
import { createProfession, updateProfession, getProfession } from '../services/professionService';
import { CreateProfessionDto } from '../types/Profession';

export default function ProfessionForm({ id, onSuccess, referenceId }: { id?: number; onSuccess: () => void; referenceId?: number }) {
  const [form] = Form.useForm<CreateProfessionDto>();
  
  const [attributeOptions, setAttributeOptions] = React.useState<any[]>([]);
  const selectedAttrIds = Form.useWatch('attributes', form) as any[] | undefined;
  const sortedAttributeOptions = React.useMemo(() => {
    const selectedSet = new Set(
      (Array.isArray(selectedAttrIds) ? selectedAttrIds : [])
        .map((value: any) => Number(value))
        .filter((value: number) => Number.isFinite(value)),
    );
    return (attributeOptions || [])
      .filter((option: any) => {
        const optionId = Number(option?.id);
        const isSelected = Number.isFinite(optionId) && selectedSet.has(optionId);
        const isActive = option?.isActive === undefined ? true : !!option?.isActive;
        return isActive || isSelected;
      })
      .slice()
      .sort((a: any, b: any) =>
        String(a?.name ?? a?.id ?? '')
          .toLocaleLowerCase('tr')
          .localeCompare(String(b?.name ?? b?.id ?? '').toLocaleLowerCase('tr'), 'tr'),
      );
  }, [attributeOptions, selectedAttrIds]);
  React.useEffect(() => {
    let cancelled = false;
    if (id) {
      (async () => {
        const d = await getProfession(id);
        const data: any = { ...d };
        const selectedAttrObjs: any[] = Array.isArray(d?.attributes) ? d.attributes : [];
        if (data.attributes) data.attributes = selectedAttrObjs.map((o: any) => o?.id ?? o).filter((v: any) => v != null);
        form.setFieldsValue(data);
        const activeOptions = await apiFetch('/attributes?isActive=true');
        const merged = Array.isArray(activeOptions) ? [...activeOptions] : [];
        // Ensure previously selected (possibly inactive) attributes appear in options
        selectedAttrObjs.forEach((o: any) => {
          const oid = o?.id ?? o;
          if (!merged.some((x: any) => (x?.id ?? x) === oid)) {
            merged.push(o);
          }
        });
        if (!cancelled) setAttributeOptions(merged);
      })();
    } else {
      form.resetFields();
      form.setFieldsValue({ isActive: true });
      apiFetch('/attributes?isActive=true').then((opts) => { if (!cancelled) setAttributeOptions(opts); });
    }
    return () => { cancelled = true; };
  }, [id, form]);
  const onFinish = async (values: CreateProfessionDto) => {
    const data: any = { ...values };
    data.name = toTurkishUpper(values.name)?.trim() ?? values.name;
    if (data.attributes) data.attributes = data.attributes.map((id: any) => id);
    try {
      if (id) {
        await updateProfession(id, data);
        openNotification('success', 'Başarılı', 'Meslek Güncellendi');
      } else {
        await createProfession(data);
        openNotification('success', 'Başarılı', 'Meslek Eklendi');
      }
      onSuccess();
    } catch (e) {
      openNotification('error', 'Hata', 'Meslek işlemi başarısız');
    }
  };
  return (
    <>
      
      <Form form={form} onFinish={onFinish} layout='vertical' initialValues={{ isActive: true }}>
      <Form.Item name='name' label='Meslek Adı' rules={[{ required: true }]}>
        <Input style={{ width: '100%', textTransform: 'uppercase' }} lang='tr' />
      </Form.Item>
      <Form.Item name='isActive' valuePropName='checked' label='Aktif/Pasif'>
        <Switch checkedChildren='Aktif' unCheckedChildren='Pasif' />
      </Form.Item>
      <Form.Item name='attributes' label='Parametre' rules={[{ required: true, message: 'Parametre seçimi zorunludur' }] }>
        <Select
          mode='multiple'
          showSearch
          filterOption={(input, option) => ((option?.children?.toString()) || '').toLocaleLowerCase('tr').includes(input.toLocaleLowerCase('tr'))}
          placeholder='Seçiniz'
          style={{width:'100%'}}
        >
          {sortedAttributeOptions
            .map((o: any) => (
              <Select.Option key={o.id} value={o.id}>{o.name ?? o.id}</Select.Option>
            ))}
        </Select>
      </Form.Item>
      <Button htmlType='submit' type='primary' block>Kaydet</Button>
    </Form>
    </>
  );
}
